import { useState, useEffect } from 'react'
import api from '../services/api'
import CidadeSelect from '../components/CidadeSelect'

const ESTILO_LABEL: React.CSSProperties = { display: 'block', minHeight: '2.5em', lineHeight: 1.2 }

function Secao({ titulo, cor, corFundo, grid, children }: {
  titulo: string; cor: string; corFundo: string; grid?: boolean; children: React.ReactNode
}) {
  return (
    <div style={{
      background: corFundo, border: '1px solid #eef0f4', borderLeft: `4px solid ${cor}`,
      borderRadius: '0.5rem', padding: '0.9rem 1.1rem', marginBottom: '0.9rem',
    }}>
      <h4 style={{ fontSize: '0.7rem', fontWeight: 700, color: cor, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.7rem' }}>
        {titulo}
      </h4>
      <div style={grid
        ? { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.6rem 1rem', maxWidth: 760 }
        : { display: 'flex', flexWrap: 'wrap', gap: '0.6rem 1rem', alignItems: 'flex-end' }}>
        {children}
      </div>
    </div>
  )
}

function Campo({ label, value, onChange, width, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; width?: number | string; placeholder?: string
}) {
  return (
    <div className="field-cad" style={{ width }}>
      <label style={ESTILO_LABEL}>{label}</label>
      <input type="text" value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} style={{ width: '100%' }} />
    </div>
  )
}

function CampoData({ label, value, onChange, width }: {
  label: string; value: string; onChange: (v: string) => void; width?: number | string
}) {
  return (
    <div className="field-cad" style={{ width }}>
      <label style={ESTILO_LABEL}>{label}</label>
      <input type="date" value={value} onChange={e => onChange(e.target.value)} style={{ width: '100%' }} />
    </div>
  )
}

function CampoTextarea({ label, value, onChange, width, linhas }: {
  label: string; value: string; onChange: (v: string) => void; width?: number | string; linhas?: number
}) {
  return (
    <div className="field-cad" style={{ width }}>
      <label style={ESTILO_LABEL}>{label}</label>
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={linhas ?? 3}
        style={{ width: '100%', fontFamily: 'inherit', fontSize: '0.85rem', padding: '0.55rem 0.75rem', border: '1.5px solid #d1d5db', borderRadius: '0.4rem', resize: 'vertical' }} />
    </div>
  )
}

interface Opcao { value: string; label: string }

function CampoSel({ label, value, onChange, opcoes, width }: {
  label: string; value: string; onChange: (v: string) => void; opcoes: Opcao[]; width?: number | string
}) {
  return (
    <div className="field-cad" style={{ width }}>
      <label style={ESTILO_LABEL}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={{ width: '100%' }}>
        {opcoes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

function CampoCheck({ label, checked, onChange, width }: {
  label: string; checked: boolean; onChange: (v: boolean) => void; width?: number | string
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width, fontSize: '0.85rem', color: '#374151', cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
      {label}
    </label>
  )
}

const OPCOES_SIM_NAO: Opcao[] = [{ value: 'S', label: 'Sim' }, { value: 'N', label: 'Não' }]

const OPCOES_TIPO_ALIQUOTA: Opcao[] = [
  { value: '1', label: 'Fixa' },
  { value: '2', label: 'Padrão' },
  { value: '3', label: 'Sem Alíquota' },
  { value: '4', label: 'Uniforme Nacional' },
  { value: '5', label: 'Uniforme Setorial' },
]

const OPCOES_CREDITO_PARA: Opcao[] = [
  { value: '', label: 'N/A' },
  { value: 'F', label: 'Fornecedor' },
  { value: 'A', label: 'Adquirente' },
]

const OPCOES_REGIME_TRIBUTARIO: Opcao[] = [
  { value: '3', label: 'Normal' },
  { value: '1', label: 'Simples Nacional' },
  { value: '2', label: 'Simples Nacional Excesso' },
  { value: '4', label: 'MEI' },
]

const OPCOES_ENTIDADE: Opcao[] = [
  { value: '1', label: 'União' },
  { value: '2', label: 'Estados' },
  { value: '3', label: 'DF' },
  { value: '4', label: 'Municípios' },
  { value: '5', label: 'Consórcio Público' },
  { value: '6', label: 'Comitê Gestor do IBS' },
]

const OPCOES_TIPO_OPERACAO_GOV: Opcao[] = [
  { value: '1', label: 'Fornecimento com Pagamento Posterior' },
  { value: '2', label: 'Recebimento do Pagamento com Fornecimento Realizado' },
  { value: '3', label: 'Fornecimento com Pagamento já Realizado' },
  { value: '4', label: 'Recebimento do Pagamento com Fornecimento Posterior' },
]

const OPCOES_DOCUMENTO_FISCAL: Opcao[] = [
  { value: '55', label: 'NFe' },
  { value: '65', label: 'NFCe' },
  { value: '57', label: 'CTe' },
  { value: '67', label: 'Cte_Os' },
  { value: '99', label: 'NFSe' },
]

const OPCOES_ALIQUOTA_CREDPRES: Opcao[] = [
  { value: '1', label: 'Alíquota calculada e divulgada anualmente' },
  { value: '2', label: 'Alíquota fixa' },
  { value: '3', label: 'Alíquota efetiva' },
  { value: '0', label: 'Nulo' },
]

const OPCOES_ALIQUOTA_IBS_CREDPRES: Opcao[] = [
  { value: '1', label: 'Alíquota calculada e divulgada anualmente' },
  { value: '2', label: 'Alíquota fixa' },
  { value: '3', label: 'Alíquota efetiva' },
  { value: '4', label: 'Alíquota efetiva com redução' },
  { value: '0', label: 'Nulo' },
]

const OPCOES_PALIQ_CREDPRES_CBS: Opcao[] = [
  { value: '1', label: 'pAliqCalculadaCBS' },
  { value: '2', label: '7%' },
  { value: '3', label: 'pAliqEfet' },
  { value: '4', label: '6%' },
  { value: '0', label: 'Nulo' },
]

const OPCOES_CCLASS_REFERENCIADA: Opcao[] = [
  { value: '0', label: 'Nulo' },
  { value: '1', label: 'Com incidência de CBS' },
  { value: '2', label: 'Com incidência de IBS' },
  { value: '3', label: 'cClass de alíquota zero' },
  { value: '4', label: '410017' },
  { value: '5', label: '410016' },
  { value: '6', label: '410015' },
  { value: '7', label: '410014' },
]

const TABS = [
  { key: 'ibs001', label: 'CST IBS/CBS', tabela: 'TIBS001' },
  { key: 'ibs002', label: 'Classificação Tributária', tabela: 'TIBS002' },
  { key: 'ibs004', label: 'Crédito Presumido', tabela: 'TIBS004' },
  { key: 'ibs006', label: 'Operações Municipais', tabela: 'TIBS006' },
  { key: 'ibs007', label: '% Destinado ao Estado', tabela: 'TIBS007' },
  { key: 'ibs008', label: 'Regras de Emissão', tabela: 'TIBS008' },
] as const

type TabKey = typeof TABS[number]['key']

export default function ReformaTributaria() {
  const [aba, setAba] = useState<TabKey>('ibs001')

  // TIBS001 — Códigos de Situação Tributária (frota.ibs001)
  const [lista001, setLista001] = useState<{ ibs01_cod: string; ibs01_des: string }[]>([])
  const [busca001, setBusca001] = useState('')
  const [carregando001, setCarregando001] = useState(false)
  const [selecionado001, setSelecionado001] = useState<{ ibs01_cod: string; ibs01_des: string } | null>(null)
  const [carregandoDetalhe001, setCarregandoDetalhe001] = useState(false)
  const [form001, setForm001] = useState({
    ibs01_des: '', ibs01_tri: 'N', ibs01_rbc: 'N', ibs01_ral: 'N',
    ibs01_tra: 'N', ibs01_dif: 'N', ibs01_mon: 'N', ibs01_zfm: 'N', ibs01_ajc: 'N',
  })
  const [salvando001, setSalvando001] = useState(false)
  const [msgSalvar001, setMsgSalvar001] = useState('')
  const [modoNovo001, setModoNovo001] = useState(false)
  const [codigoNovo001, setCodigoNovo001] = useState('')

  const FORM001_VAZIO = {
    ibs01_des: '', ibs01_tri: 'N', ibs01_rbc: 'N', ibs01_ral: 'N',
    ibs01_tra: 'N', ibs01_dif: 'N', ibs01_mon: 'N', ibs01_zfm: 'N', ibs01_ajc: 'N',
  }

  function novo001() {
    setModoNovo001(true)
    setSelecionado001(null)
    setMsgSalvar001('')
    setCodigoNovo001('')
    setForm001(FORM001_VAZIO)
  }

  async function carregarLista001(termo: string) {
    setCarregando001(true)
    try {
      const r = await api.get('/erp/ibs001/lista', { params: termo ? { busca: termo } : {} })
      setLista001(r.data)
    } finally {
      setCarregando001(false)
    }
  }

  useEffect(() => { carregarLista001('') }, [])
  useEffect(() => {
    const t = setTimeout(() => carregarLista001(busca001), 350)
    return () => clearTimeout(t)
  }, [busca001])

  async function selecionar001(cod: string) {
    setModoNovo001(false)
    setCarregandoDetalhe001(true)
    setMsgSalvar001('')
    try {
      const r = await api.get(`/erp/ibs001/${cod}`)
      setSelecionado001(r.data)
      setForm001({
        ibs01_des: r.data.ibs01_des ?? '',
        ibs01_tri: r.data.ibs01_tri || 'N',
        ibs01_rbc: r.data.ibs01_rbc || 'N',
        ibs01_ral: r.data.ibs01_ral || 'N',
        ibs01_tra: r.data.ibs01_tra || 'N',
        ibs01_dif: r.data.ibs01_dif || 'N',
        ibs01_mon: r.data.ibs01_mon || 'N',
        ibs01_zfm: r.data.ibs01_zfm || 'N',
        ibs01_ajc: r.data.ibs01_ajc || 'N',
      })
    } finally {
      setCarregandoDetalhe001(false)
    }
  }

  async function salvar001() {
    if (modoNovo001) {
      if (!codigoNovo001.trim()) {
        setMsgSalvar001('Informe o código.')
        return
      }
      setSalvando001(true)
      setMsgSalvar001('')
      try {
        await api.post('/erp/ibs001', { ibs01_cod: codigoNovo001.trim(), ...form001 })
        await carregarLista001(busca001)
        setModoNovo001(false)
        await selecionar001(codigoNovo001.trim())
        setMsgSalvar001('Código cadastrado com sucesso.')
      } catch (e: any) {
        setMsgSalvar001(e.response?.data?.error ?? 'Erro ao cadastrar.')
      } finally {
        setSalvando001(false)
      }
      return
    }

    if (!selecionado001) return
    setSalvando001(true)
    setMsgSalvar001('')
    try {
      await api.put(`/erp/ibs001/${selecionado001.ibs01_cod}`, form001)
      setMsgSalvar001('Alterações salvas com sucesso.')
      await carregarLista001(busca001)
    } catch (e: any) {
      setMsgSalvar001(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando001(false)
    }
  }

  // TIBS002 — Códigos de Classificação Tributária (frota.ibs002, chave: cst + cod)
  const [lista002, setLista002] = useState<{ ibs02_cst: string; ibs02_cod: string; ibs02_des: string }[]>([])
  const [busca002, setBusca002] = useState('')
  const [carregando002, setCarregando002] = useState(false)
  const [opcoesCstIbs001, setOpcoesCstIbs001] = useState<Opcao[]>([])
  const [selecionado002, setSelecionado002] = useState<{ ibs02_cst: string; ibs02_cod: string } | null>(null)
  const [carregandoDetalhe002, setCarregandoDetalhe002] = useState(false)
  const FORM002_VAZIO = {
    ibs02_des: '', ibs02_com: '', ibs02_rib: '', ibs02_rcb: '',
    ibs02_bas: 'N', ibs02_tre: 'N', ibs02_pre: 'N', ibs02_anx: '', ibs02_ecr: 'N', ibs02_ali: '', ibs02_lc: '',
    ibs02_mop: '', ibs02_mor: '', ibs02_moe: '', ibs02_dif: '', ibs02_crp: '',
    ibs02_ivi: '', ibs02_fvi: '', ibs02_dat: '',
  }
  const [form002, setForm002] = useState(FORM002_VAZIO)
  const [salvando002, setSalvando002] = useState(false)
  const [msgSalvar002, setMsgSalvar002] = useState('')
  const [modoNovo002, setModoNovo002] = useState(false)
  const [cstNovo002, setCstNovo002] = useState('')
  const [codNovo002, setCodNovo002] = useState('')

  async function carregarLista002(termo: string) {
    setCarregando002(true)
    try {
      const r = await api.get('/erp/ibs002/lista', { params: termo ? { busca: termo } : {} })
      setLista002(r.data)
    } finally {
      setCarregando002(false)
    }
  }

  useEffect(() => {
    carregarLista002('')
    api.get('/erp/ibs001/lista').then(r => {
      setOpcoesCstIbs001(r.data.map((o: any) => ({ value: String(o.ibs01_cod), label: `${o.ibs01_cod} - ${o.ibs01_des}` })))
    })
  }, [])
  useEffect(() => {
    const t = setTimeout(() => carregarLista002(busca002), 350)
    return () => clearTimeout(t)
  }, [busca002])

  function novo002() {
    setModoNovo002(true)
    setSelecionado002(null)
    setMsgSalvar002('')
    setCstNovo002('')
    setCodNovo002('')
    setForm002(FORM002_VAZIO)
  }

  async function selecionar002(cst: string, cod: string) {
    setModoNovo002(false)
    setCarregandoDetalhe002(true)
    setMsgSalvar002('')
    try {
      const r = await api.get(`/erp/ibs002/${cst}/${cod}`)
      setSelecionado002({ ibs02_cst: r.data.ibs02_cst, ibs02_cod: r.data.ibs02_cod })
      setForm002({
        ibs02_des: r.data.ibs02_des ?? '',
        ibs02_com: r.data.ibs02_com ?? '',
        ibs02_rib: r.data.ibs02_rib ?? '',
        ibs02_rcb: r.data.ibs02_rcb ?? '',
        ibs02_bas: r.data.ibs02_bas || 'N',
        ibs02_tre: r.data.ibs02_tre || 'N',
        ibs02_pre: r.data.ibs02_pre || 'N',
        ibs02_anx: r.data.ibs02_anx ?? '',
        ibs02_ecr: r.data.ibs02_ecr || 'N',
        ibs02_ali: r.data.ibs02_ali ?? '',
        ibs02_lc: r.data.ibs02_lc ?? '',
        ibs02_mop: r.data.ibs02_mop ?? '',
        ibs02_mor: r.data.ibs02_mor ?? '',
        ibs02_moe: r.data.ibs02_moe ?? '',
        ibs02_dif: r.data.ibs02_dif ?? '',
        ibs02_crp: r.data.ibs02_crp ?? '',
        ibs02_ivi: r.data.ibs02_ivi ? String(r.data.ibs02_ivi).slice(0, 10) : '',
        ibs02_fvi: r.data.ibs02_fvi ? String(r.data.ibs02_fvi).slice(0, 10) : '',
        ibs02_dat: r.data.ibs02_dat ? String(r.data.ibs02_dat).slice(0, 10) : '',
      })
    } finally {
      setCarregandoDetalhe002(false)
    }
  }

  async function salvar002() {
    if (modoNovo002) {
      if (!cstNovo002.trim() || !codNovo002.trim()) {
        setMsgSalvar002('Informe o CST IBS/CBS e o cClassTrib.')
        return
      }
      setSalvando002(true)
      setMsgSalvar002('')
      try {
        await api.post('/erp/ibs002', { ibs02_cst: cstNovo002.trim(), ibs02_cod: codNovo002.trim(), ...form002 })
        await carregarLista002(busca002)
        setModoNovo002(false)
        await selecionar002(cstNovo002.trim(), codNovo002.trim())
        setMsgSalvar002('cClassTrib cadastrado com sucesso.')
      } catch (e: any) {
        setMsgSalvar002(e.response?.data?.error ?? 'Erro ao cadastrar.')
      } finally {
        setSalvando002(false)
      }
      return
    }

    if (!selecionado002) return
    setSalvando002(true)
    setMsgSalvar002('')
    try {
      await api.put(`/erp/ibs002/${selecionado002.ibs02_cst}/${selecionado002.ibs02_cod}`, form002)
      setMsgSalvar002('Alterações salvas com sucesso.')
      await carregarLista002(busca002)
    } catch (e: any) {
      setMsgSalvar002(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando002(false)
    }
  }

  // TIBS004 — Tabela de Crédito Presumido (frota.ibs004)
  const [lista004, setLista004] = useState<{ ibs04_cod: string; ibs04_des: string }[]>([])
  const [busca004, setBusca004] = useState('')
  const [carregando004, setCarregando004] = useState(false)
  const [selecionado004, setSelecionado004] = useState<{ ibs04_cod: string; ibs04_des: string } | null>(null)
  const [carregandoDetalhe004, setCarregandoDetalhe004] = useState(false)
  const FORM004_VAZIO = {
    ibs04_des: '', ibs04_apd: 'N', ibs04_ape: 'N', ibs04_dvt: 'N', ibs04_ibs: 'N', ibs04_cbs: 'N',
    ibs04_acb: '', ibs04_aib: '', ibs04_ivg: '', ibs04_fvg: '', ibs04_ivi: '', ibs04_fvi: '',
    ibs04_pal: '', ibs04_cla: '',
  }
  const [form004, setForm004] = useState(FORM004_VAZIO)
  const [salvando004, setSalvando004] = useState(false)
  const [msgSalvar004, setMsgSalvar004] = useState('')
  const [modoNovo004, setModoNovo004] = useState(false)
  const [codigoNovo004, setCodigoNovo004] = useState('')

  async function carregarLista004(termo: string) {
    setCarregando004(true)
    try {
      const r = await api.get('/erp/ibs004/lista', { params: termo ? { busca: termo } : {} })
      setLista004(r.data)
    } finally {
      setCarregando004(false)
    }
  }

  useEffect(() => { carregarLista004('') }, [])
  useEffect(() => {
    const t = setTimeout(() => carregarLista004(busca004), 350)
    return () => clearTimeout(t)
  }, [busca004])

  function novo004() {
    setModoNovo004(true)
    setSelecionado004(null)
    setMsgSalvar004('')
    setCodigoNovo004('')
    setForm004(FORM004_VAZIO)
  }

  function dataParaInput(v: any) {
    return v ? String(v).slice(0, 10) : ''
  }

  async function selecionar004(cod: string) {
    setModoNovo004(false)
    setCarregandoDetalhe004(true)
    setMsgSalvar004('')
    try {
      const r = await api.get(`/erp/ibs004/${cod}`)
      setSelecionado004({ ibs04_cod: r.data.ibs04_cod, ibs04_des: r.data.ibs04_des })
      setForm004({
        ibs04_des: r.data.ibs04_des ?? '',
        ibs04_apd: r.data.ibs04_apd || 'N',
        ibs04_ape: r.data.ibs04_ape || 'N',
        ibs04_dvt: r.data.ibs04_dvt || 'N',
        ibs04_ibs: r.data.ibs04_ibs || 'N',
        ibs04_cbs: r.data.ibs04_cbs || 'N',
        ibs04_acb: r.data.ibs04_acb ?? '',
        ibs04_aib: r.data.ibs04_aib ?? '',
        ibs04_ivg: dataParaInput(r.data.ibs04_ivg),
        ibs04_fvg: dataParaInput(r.data.ibs04_fvg),
        ibs04_ivi: dataParaInput(r.data.ibs04_ivi),
        ibs04_fvi: dataParaInput(r.data.ibs04_fvi),
        ibs04_pal: r.data.ibs04_pal ?? '',
        ibs04_cla: r.data.ibs04_cla ?? '',
      })
    } finally {
      setCarregandoDetalhe004(false)
    }
  }

  async function salvar004() {
    if (modoNovo004) {
      if (!codigoNovo004.trim()) {
        setMsgSalvar004('Informe o código.')
        return
      }
      setSalvando004(true)
      setMsgSalvar004('')
      try {
        await api.post('/erp/ibs004', { ibs04_cod: codigoNovo004.trim(), ...form004 })
        await carregarLista004(busca004)
        setModoNovo004(false)
        await selecionar004(codigoNovo004.trim())
        setMsgSalvar004('Código cadastrado com sucesso.')
      } catch (e: any) {
        setMsgSalvar004(e.response?.data?.error ?? 'Erro ao cadastrar.')
      } finally {
        setSalvando004(false)
      }
      return
    }

    if (!selecionado004) return
    setSalvando004(true)
    setMsgSalvar004('')
    try {
      await api.put(`/erp/ibs004/${selecionado004.ibs04_cod}`, form004)
      setMsgSalvar004('Alterações salvas com sucesso.')
      await carregarLista004(busca004)
    } catch (e: any) {
      setMsgSalvar004(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando004(false)
    }
  }

  // TIBS006 — Tabela de Operações Municipais (INDOP, frota.ibs006)
  const [lista006, setLista006] = useState<{ ibs06_cod: string; ibs06_top: string }[]>([])
  const [busca006, setBusca006] = useState('')
  const [carregando006, setCarregando006] = useState(false)
  const [selecionado006, setSelecionado006] = useState<{ ibs06_cod: string; ibs06_top: string } | null>(null)
  const [carregandoDetalhe006, setCarregandoDetalhe006] = useState(false)
  const FORM006_VAZIO = { ibs06_bas: '', ibs06_top: '', ibs06_lop: '', ibs06_cfo: '', ibs06_lfo: '' }
  const [form006, setForm006] = useState(FORM006_VAZIO)
  const [salvando006, setSalvando006] = useState(false)
  const [msgSalvar006, setMsgSalvar006] = useState('')
  const [modoNovo006, setModoNovo006] = useState(false)
  const [codigoNovo006, setCodigoNovo006] = useState('')

  async function carregarLista006(termo: string) {
    setCarregando006(true)
    try {
      const r = await api.get('/erp/ibs006/lista', { params: termo ? { busca: termo } : {} })
      setLista006(r.data)
    } finally {
      setCarregando006(false)
    }
  }

  useEffect(() => { carregarLista006('') }, [])
  useEffect(() => {
    const t = setTimeout(() => carregarLista006(busca006), 350)
    return () => clearTimeout(t)
  }, [busca006])

  function novo006() {
    setModoNovo006(true)
    setSelecionado006(null)
    setMsgSalvar006('')
    setCodigoNovo006('')
    setForm006(FORM006_VAZIO)
  }

  async function selecionar006(cod: string) {
    setModoNovo006(false)
    setCarregandoDetalhe006(true)
    setMsgSalvar006('')
    try {
      const r = await api.get(`/erp/ibs006/${cod}`)
      setSelecionado006({ ibs06_cod: r.data.ibs06_cod, ibs06_top: r.data.ibs06_top })
      setForm006({
        ibs06_bas: r.data.ibs06_bas ?? '',
        ibs06_top: r.data.ibs06_top ?? '',
        ibs06_lop: r.data.ibs06_lop ?? '',
        ibs06_cfo: r.data.ibs06_cfo ?? '',
        ibs06_lfo: r.data.ibs06_lfo ?? '',
      })
    } finally {
      setCarregandoDetalhe006(false)
    }
  }

  async function salvar006() {
    if (modoNovo006) {
      if (!codigoNovo006.trim()) {
        setMsgSalvar006('Informe o código.')
        return
      }
      setSalvando006(true)
      setMsgSalvar006('')
      try {
        await api.post('/erp/ibs006', { ibs06_cod: codigoNovo006.trim(), ...form006 })
        await carregarLista006(busca006)
        setModoNovo006(false)
        await selecionar006(codigoNovo006.trim())
        setMsgSalvar006('Código cadastrado com sucesso.')
      } catch (e: any) {
        setMsgSalvar006(e.response?.data?.error ?? 'Erro ao cadastrar.')
      } finally {
        setSalvando006(false)
      }
      return
    }

    if (!selecionado006) return
    setSalvando006(true)
    setMsgSalvar006('')
    try {
      await api.put(`/erp/ibs006/${selecionado006.ibs06_cod}`, form006)
      setMsgSalvar006('Alterações salvas com sucesso.')
      await carregarLista006(busca006)
    } catch (e: any) {
      setMsgSalvar006(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando006(false)
    }
  }

  // TIBS007 — pIBSUF Percentual destinado ao Estado (frota.ibs007 + vigências em frota.ibs005; nome vem de frota.estado)
  const [lista007, setLista007] = useState<{ ibs07_est: string; ibs07_nom: string }[]>([])
  const [busca007, setBusca007] = useState('')
  const [carregando007, setCarregando007] = useState(false)
  const [selecionado007, setSelecionado007] = useState<{ ibs07_est: string; ibs07_nom: string } | null>(null)
  const [carregandoDetalhe007, setCarregandoDetalhe007] = useState(false)
  const [nome007, setNome007] = useState('')
  const [linhas007, setLinhas007] = useState([{ ibs07_ivi: '', ibs07_fvi: '', ibs07_per: '' }])
  const [salvando007, setSalvando007] = useState(false)
  const [msgSalvar007, setMsgSalvar007] = useState('')
  const [modoNovo007, setModoNovo007] = useState(false)
  const [codigoNovo007, setCodigoNovo007] = useState('')
  const [estados, setEstados] = useState<{ estcod: string; estnom: string }[]>([])

  async function carregarLista007(termo: string) {
    setCarregando007(true)
    try {
      const r = await api.get('/erp/ibs007/lista', { params: termo ? { busca: termo } : {} })
      setLista007(r.data)
    } finally {
      setCarregando007(false)
    }
  }

  useEffect(() => { carregarLista007('') }, [])
  useEffect(() => {
    const t = setTimeout(() => carregarLista007(busca007), 350)
    return () => clearTimeout(t)
  }, [busca007])
  useEffect(() => {
    api.get('/erp/estado/lista').then(r => setEstados(r.data))
  }, [])

  function novo007() {
    setModoNovo007(true)
    setSelecionado007(null)
    setMsgSalvar007('')
    setCodigoNovo007('')
    setNome007('')
    setLinhas007([{ ibs07_ivi: '', ibs07_fvi: '', ibs07_per: '' }])
  }

  function selecionarUfNova(cod: string) {
    setCodigoNovo007(cod)
    setNome007(estados.find(e => e.estcod === cod)?.estnom ?? '')
  }

  async function selecionar007(est: string) {
    setModoNovo007(false)
    setCarregandoDetalhe007(true)
    setMsgSalvar007('')
    try {
      const r = await api.get(`/erp/ibs007/${est}`)
      setSelecionado007({ ibs07_est: r.data.ibs07_est, ibs07_nom: r.data.ibs07_nom })
      setNome007(r.data.ibs07_nom ?? '')
      setLinhas007(
        r.data.vigencias?.length
          ? r.data.vigencias.map((v: any) => ({
              ibs07_ivi: dataParaInput(v.ibs07_ivi),
              ibs07_fvi: dataParaInput(v.ibs07_fvi),
              ibs07_per: v.ibs07_per ?? '',
            }))
          : [{ ibs07_ivi: '', ibs07_fvi: '', ibs07_per: '' }]
      )
    } finally {
      setCarregandoDetalhe007(false)
    }
  }

  async function salvar007() {
    const vigencias = linhas007.filter(l => l.ibs07_ivi)
    if (modoNovo007) {
      if (!codigoNovo007.trim()) {
        setMsgSalvar007('Informe o código da UF.')
        return
      }
      setSalvando007(true)
      setMsgSalvar007('')
      try {
        await api.post('/erp/ibs007', { ibs07_est: codigoNovo007.trim(), vigencias })
        await carregarLista007(busca007)
        setModoNovo007(false)
        await selecionar007(codigoNovo007.trim())
        setMsgSalvar007('UF cadastrada com sucesso.')
      } catch (e: any) {
        setMsgSalvar007(e.response?.data?.error ?? 'Erro ao cadastrar.')
      } finally {
        setSalvando007(false)
      }
      return
    }

    if (!selecionado007) return
    setSalvando007(true)
    setMsgSalvar007('')
    try {
      await api.put(`/erp/ibs007/${selecionado007.ibs07_est}`, { vigencias })
      setMsgSalvar007('Alterações salvas com sucesso.')
      await carregarLista007(busca007)
    } catch (e: any) {
      setMsgSalvar007(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando007(false)
    }
  }

  // TIBS008 — Regras IBS/CBS/IS para Emissão de NF's (frota.ibs008)
  const [lista008, setLista008] = useState<{ ibs08_cod: number; ibs08_des: string }[]>([])
  const [busca008, setBusca008] = useState('')
  const [carregando008, setCarregando008] = useState(false)
  const [selecionado008, setSelecionado008] = useState<{ ibs08_cod: number; ibs08_des: string } | null>(null)
  const [carregandoDetalhe008, setCarregandoDetalhe008] = useState(false)
  const FORM008_VAZIO = {
    ibs08_des: '', ibs08_cst: '', ibs08_tri: '', ibs08_dfe: '', ibs08_gub: 'N', ibs08_gov: 'N', ibs08_tra: 'N',
    ibs08_crt: '', ibs08_ent: '', ibs08_ivi: '', ibs08_fvi: '',
    ibs08_cali: '', ibs08_crbc: '', ibs08_cpra: '', ibs08_cpdi: '', ibs08_cccp: '', ibs08_cpcp: '',
    ibs08_uest: '', ibs08_uali: '', ibs08_urbc: '', ibs08_upra: '', ibs08_updi: '', ibs08_uccp: '', ibs08_upcp: '',
    ibs08_mmun: '', ibs08_mibg: '', ibs08_mali: '', ibs08_mrbc: '', ibs08_mpra: '', ibs08_mpdi: '', ibs08_mccp: '', ibs08_mpcp: '',
    ibs08_sbas: '', ibs08_sali: '', ibs08_top: '',
  }
  const [form008, setForm008] = useState(FORM008_VAZIO)
  const [desc008, setDesc008] = useState({ ibs08_d01: '', ibs08_d02: '', ibs08_unom: '', ibs08_mnom: '' })
  const [salvando008, setSalvando008] = useState(false)
  const [msgSalvar008, setMsgSalvar008] = useState('')
  const [modoNovo008, setModoNovo008] = useState(false)
  const [codigoNovo008, setCodigoNovo008] = useState('')
  const [subAba008, setSubAba008] = useState<'cbs' | 'ibsuf' | 'ibsmun' | 'is'>('cbs')
  const [listaCst008, setListaCst008] = useState<{ ibs01_cod: string; ibs01_des: string }[]>([])
  const [listaTri008, setListaTri008] = useState<{ ibs02_cst: string; ibs02_cod: string; ibs02_des: string }[]>([])

  useEffect(() => {
    api.get('/erp/ibs001/lista').then(r => setListaCst008(r.data))
    api.get('/erp/ibs002/lista').then(r => setListaTri008(r.data))
  }, [])

  async function carregarLista008(termo: string) {
    setCarregando008(true)
    try {
      const r = await api.get('/erp/ibs008/lista', { params: termo ? { busca: termo } : {} })
      setLista008(r.data)
    } finally {
      setCarregando008(false)
    }
  }

  useEffect(() => { carregarLista008('') }, [])
  useEffect(() => {
    const t = setTimeout(() => carregarLista008(busca008), 350)
    return () => clearTimeout(t)
  }, [busca008])

  function novo008() {
    setModoNovo008(true)
    setSelecionado008(null)
    setMsgSalvar008('')
    setCodigoNovo008('')
    setForm008(FORM008_VAZIO)
    setDesc008({ ibs08_d01: '', ibs08_d02: '', ibs08_unom: '', ibs08_mnom: '' })
    setSubAba008('cbs')
  }

  async function selecionar008(cod: number | string) {
    setModoNovo008(false)
    setCarregandoDetalhe008(true)
    setMsgSalvar008('')
    try {
      const r = await api.get(`/erp/ibs008/${cod}`)
      const d = r.data
      setSelecionado008({ ibs08_cod: d.ibs08_cod, ibs08_des: d.ibs08_des })
      setForm008({
        ibs08_des: d.ibs08_des ?? '', ibs08_cst: d.ibs08_cst ?? '', ibs08_tri: d.ibs08_tri ?? '',
        ibs08_dfe: d.ibs08_dfe ?? '', ibs08_gub: d.ibs08_gub || 'N', ibs08_gov: d.ibs08_gov || 'N', ibs08_tra: d.ibs08_tra || 'N',
        ibs08_crt: d.ibs08_crt ?? '', ibs08_ent: d.ibs08_ent ?? '',
        ibs08_ivi: dataParaInput(d.ibs08_ivi), ibs08_fvi: dataParaInput(d.ibs08_fvi),
        ibs08_cali: d.ibs08_cali ?? '', ibs08_crbc: d.ibs08_crbc ?? '', ibs08_cpra: d.ibs08_cpra ?? '',
        ibs08_cpdi: d.ibs08_cpdi ?? '', ibs08_cccp: d.ibs08_cccp ?? '', ibs08_cpcp: d.ibs08_cpcp ?? '',
        ibs08_uest: d.ibs08_uest ?? '', ibs08_uali: d.ibs08_uali ?? '', ibs08_urbc: d.ibs08_urbc ?? '',
        ibs08_upra: d.ibs08_upra ?? '', ibs08_updi: d.ibs08_updi ?? '', ibs08_uccp: d.ibs08_uccp ?? '', ibs08_upcp: d.ibs08_upcp ?? '',
        ibs08_mmun: d.ibs08_mmun ?? '', ibs08_mibg: d.ibs08_mibg ?? '', ibs08_mali: d.ibs08_mali ?? '',
        ibs08_mrbc: d.ibs08_mrbc ?? '', ibs08_mpra: d.ibs08_mpra ?? '', ibs08_mpdi: d.ibs08_mpdi ?? '',
        ibs08_mccp: d.ibs08_mccp ?? '', ibs08_mpcp: d.ibs08_mpcp ?? '',
        ibs08_sbas: d.ibs08_sbas ?? '', ibs08_sali: d.ibs08_sali ?? '', ibs08_top: d.ibs08_top ?? '',
      })
      setDesc008({
        ibs08_d01: d.ibs08_d01 ?? '', ibs08_d02: d.ibs08_d02 ?? '',
        ibs08_unom: d.ibs08_unom ?? '', ibs08_mnom: d.ibs08_mnom ?? '',
      })
    } finally {
      setCarregandoDetalhe008(false)
    }
  }

  async function salvar008() {
    const derivados = {
      ibs08_caef: form008.ibs08_cali && form008.ibs08_crbc
        ? String(Number(form008.ibs08_cali) * (1 - Number(form008.ibs08_crbc) / 100)) : '',
      ibs08_uaef: form008.ibs08_uali && form008.ibs08_urbc
        ? String(Number(form008.ibs08_uali) * (1 - Number(form008.ibs08_urbc) / 100)) : '',
      ibs08_maef: form008.ibs08_mali && form008.ibs08_mrbc
        ? String(Number(form008.ibs08_mali) * (1 - Number(form008.ibs08_mrbc) / 100)) : '',
    }
    if (modoNovo008) {
      if (!codigoNovo008.trim()) {
        setMsgSalvar008('Informe o código da regra.')
        return
      }
      setSalvando008(true)
      setMsgSalvar008('')
      try {
        await api.post('/erp/ibs008', { ibs08_cod: codigoNovo008.trim(), ...form008, ...derivados })
        await carregarLista008(busca008)
        setModoNovo008(false)
        await selecionar008(codigoNovo008.trim())
        setMsgSalvar008('Regra cadastrada com sucesso.')
      } catch (e: any) {
        setMsgSalvar008(e.response?.data?.error ?? 'Erro ao cadastrar.')
      } finally {
        setSalvando008(false)
      }
      return
    }

    if (!selecionado008) return
    setSalvando008(true)
    setMsgSalvar008('')
    try {
      await api.put(`/erp/ibs008/${selecionado008.ibs08_cod}`, { ...form008, ...derivados })
      setMsgSalvar008('Alterações salvas com sucesso.')
      await carregarLista008(busca008)
    } catch (e: any) {
      setMsgSalvar008(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando008(false)
    }
  }

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a6ea8', marginBottom: '0.3rem' }}>
          Reforma Tributária — IBS / CBS / IS
        </h3>
        <p style={{ fontSize: '0.82rem', color: '#6b7280' }}>
          Todas as telas (TIBS001, TIBS002, TIBS004, TIBS006, TIBS007 e TIBS008) já estão ligadas ao banco (busca, consulta e edição).
        </p>
      </div>

      <div className="card" style={{ marginBottom: (aba === 'ibs001' || aba === 'ibs002' || aba === 'ibs004' || aba === 'ibs006' || aba === 'ibs007' || aba === 'ibs008') ? '1rem' : undefined }}>
        <div className="fiscal-tabs" style={{ flexWrap: 'wrap', display: 'flex', alignItems: 'center' }}>
          {TABS.map(t => (
            <button key={t.key} type="button"
              className={`fiscal-tab${aba === t.key ? ' fiscal-tab--ativa' : ''}`}
              onClick={() => setAba(t.key)}>
              {t.label}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>
            {TABS.find(t => t.key === aba)?.tabela}
          </span>
        </div>

        {aba === 'ibs006' && (
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginTop: '1rem' }}>
            <div className="card" style={{ width: 340, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a6ea8' }}>
                  Operações Municipais
                </h3>
                <button type="button" className="btn btn-primary btn-sm" onClick={novo006}>+ Novo</button>
              </div>
              <div className="field" style={{ marginBottom: '0.75rem' }}>
                <input
                  type="text"
                  placeholder="Buscar por código ou tipo de operação..."
                  value={busca006}
                  onChange={e => setBusca006(e.target.value)}
                />
              </div>

              {carregando006 && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Carregando...</p>}
              {!carregando006 && lista006.length === 0 && (
                <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhum código encontrado.</p>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
                {lista006.map(item => {
                  const ativo = selecionado006?.ibs06_cod === item.ibs06_cod
                  return (
                    <button
                      key={item.ibs06_cod}
                      onClick={() => selecionar006(item.ibs06_cod)}
                      style={{
                        textAlign: 'left', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', cursor: 'pointer',
                        background: ativo ? '#eff6ff' : '#f8fafc', borderColor: ativo ? '#1a6ea8' : '#e5e7eb',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a6ea8' }}>{item.ibs06_cod}</div>
                      <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: 2 }}>{item.ibs06_top}</div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="card" style={{ flex: 1, minWidth: 0 }}>
              {!selecionado006 && carregandoDetalhe006 && (
                <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Carregando detalhes...</p>
              )}
              {!selecionado006 && !carregandoDetalhe006 && !modoNovo006 && (
                <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                  Selecione um código na lista ao lado para ver os detalhes.
                </p>
              )}

              {(selecionado006 || modoNovo006) && (
                <>
                  <h3 style={{
                    fontSize: '1.05rem', fontWeight: 700, color: '#1a6ea8',
                    marginBottom: '1rem', paddingBottom: '0.9rem', borderBottom: '2px solid #eff6ff',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    {modoNovo006 ? 'Novo código' : selecionado006?.ibs06_cod}
                    {carregandoDetalhe006 && (
                      <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#9ca3af', textTransform: 'none' }}>
                        atualizando...
                      </span>
                    )}
                  </h3>

                  <Secao titulo="Operação" cor="#1a6ea8" corFundo="#f7faff">
                    {modoNovo006 ? (
                      <Campo label="Código IndOp" value={codigoNovo006} onChange={setCodigoNovo006} width={140} placeholder="000000" />
                    ) : (
                      <Campo label="Código IndOp" value={selecionado006?.ibs06_cod ?? ''} onChange={() => {}} width={140} />
                    )}
                    <Campo label="Base Legal" value={form006.ibs06_bas} onChange={v => setForm006(f => ({ ...f, ibs06_bas: v }))} width={200} />
                    <Campo label="Tipo de Operação" value={form006.ibs06_top} onChange={v => setForm006(f => ({ ...f, ibs06_top: v }))} width="100%" />
                    <Campo label="Local da Operação" value={form006.ibs06_lop} onChange={v => setForm006(f => ({ ...f, ibs06_lop: v }))} width="100%" />
                    <CampoTextarea label="Característica do Fornecimento" value={form006.ibs06_cfo} onChange={v => setForm006(f => ({ ...f, ibs06_cfo: v }))} width="100%" linhas={3} />
                    <Campo label="Local Fornecimento" value={form006.ibs06_lfo} onChange={v => setForm006(f => ({ ...f, ibs06_lfo: v }))} width="100%" />
                  </Secao>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '1rem' }}>
                    <button className="btn btn-primary" onClick={salvar006} disabled={salvando006}>
                      {salvando006 ? 'Salvando...' : modoNovo006 ? 'Cadastrar' : 'Salvar alterações'}
                    </button>
                    {msgSalvar006 && (
                      <span style={{
                        fontSize: '0.8rem', fontWeight: 600, padding: '0.35rem 0.8rem', borderRadius: 6,
                        background: msgSalvar006.includes('sucesso') ? '#d1fae5' : '#fee2e2',
                        color: msgSalvar006.includes('sucesso') ? '#065f46' : '#991b1b',
                      }}>
                        {msgSalvar006}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {aba === 'ibs007' && (
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginTop: '1rem' }}>
            <div className="card" style={{ width: 340, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a6ea8' }}>
                  % Destinado ao Estado
                </h3>
                <button type="button" className="btn btn-primary btn-sm" onClick={novo007}>+ Novo</button>
              </div>
              <div className="field" style={{ marginBottom: '0.75rem' }}>
                <input
                  type="text"
                  placeholder="Buscar por UF ou nome..."
                  value={busca007}
                  onChange={e => setBusca007(e.target.value)}
                />
              </div>

              {carregando007 && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Carregando...</p>}
              {!carregando007 && lista007.length === 0 && (
                <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhuma UF encontrada.</p>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
                {lista007.map(item => {
                  const ativo = selecionado007?.ibs07_est === item.ibs07_est
                  return (
                    <button
                      key={item.ibs07_est}
                      onClick={() => selecionar007(item.ibs07_est)}
                      style={{
                        textAlign: 'left', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', cursor: 'pointer',
                        background: ativo ? '#eff6ff' : '#f8fafc', borderColor: ativo ? '#1a6ea8' : '#e5e7eb',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a6ea8' }}>{item.ibs07_est}</div>
                      <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: 2 }}>{item.ibs07_nom}</div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="card" style={{ flex: 1, minWidth: 0 }}>
              {!selecionado007 && carregandoDetalhe007 && (
                <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Carregando detalhes...</p>
              )}
              {!selecionado007 && !carregandoDetalhe007 && !modoNovo007 && (
                <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                  Selecione uma UF na lista ao lado para ver os detalhes.
                </p>
              )}

              {(selecionado007 || modoNovo007) && (
                <>
                  <h3 style={{
                    fontSize: '1.05rem', fontWeight: 700, color: '#1a6ea8',
                    marginBottom: '1rem', paddingBottom: '0.9rem', borderBottom: '2px solid #eff6ff',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    {modoNovo007 ? 'Nova UF' : selecionado007?.ibs07_est}
                    {carregandoDetalhe007 && (
                      <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#9ca3af', textTransform: 'none' }}>
                        atualizando...
                      </span>
                    )}
                  </h3>

                  <Secao titulo="Identificação" cor="#1a6ea8" corFundo="#f7faff">
                    {modoNovo007 ? (
                      <CampoSel label="Código (UF)" value={codigoNovo007} onChange={selecionarUfNova} width={220}
                        opcoes={[{ value: '', label: 'Selecione...' }, ...estados.map(e => ({ value: e.estcod, label: `${e.estcod} — ${e.estnom}` }))]} />
                    ) : (
                      <Campo label="Código (UF)" value={selecionado007?.ibs07_est ?? ''} onChange={() => {}} width={70} />
                    )}
                    <Campo label="Nome" value={nome007} onChange={() => {}} width={180} />
                  </Secao>

                  <div style={{ background: '#fafafa', border: '1px solid #eef0f4', borderLeft: '4px solid #f07820', borderRadius: '0.5rem', padding: '0.9rem 1.1rem' }}>
                    <h4 style={{ fontSize: '0.7rem', fontWeight: 700, color: '#f07820', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.7rem' }}>
                      Vigências do Percentual
                    </h4>
                    <table style={{ width: 'auto', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: '#f1f5f9' }}>
                          <th style={{ width: 150, padding: '0.4rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Início Vig.</th>
                          <th style={{ width: 150, padding: '0.4rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Fim Vig.</th>
                          <th style={{ width: 110, padding: '0.4rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>pIBSUF (%)</th>
                          <th style={{ width: 40, borderBottom: '1px solid #e2e8f0' }} />
                        </tr>
                      </thead>
                      <tbody>
                        {linhas007.map((l, i) => (
                          <tr key={i}>
                            <td style={{ padding: '0.3rem 0.6rem' }}>
                              <input type="date" value={l.ibs07_ivi} style={{ width: '100%' }}
                                onChange={e => setLinhas007(ls => ls.map((x, j) => j === i ? { ...x, ibs07_ivi: e.target.value } : x))} />
                            </td>
                            <td style={{ padding: '0.3rem 0.6rem' }}>
                              <input type="date" value={l.ibs07_fvi} style={{ width: '100%' }}
                                onChange={e => setLinhas007(ls => ls.map((x, j) => j === i ? { ...x, ibs07_fvi: e.target.value } : x))} />
                            </td>
                            <td style={{ padding: '0.3rem 0.6rem' }}>
                              <input type="text" value={l.ibs07_per} style={{ width: '100%' }}
                                onChange={e => setLinhas007(ls => ls.map((x, j) => j === i ? { ...x, ibs07_per: e.target.value } : x))} />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button type="button" className="btn btn-secondary btn-sm"
                                onClick={() => setLinhas007(ls => ls.filter((_, j) => j !== i))}>✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: '0.6rem' }}
                      onClick={() => setLinhas007(ls => [...ls, { ibs07_ivi: '', ibs07_fvi: '', ibs07_per: '' }])}>
                      + Adicionar vigência
                    </button>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '1rem' }}>
                    <button className="btn btn-primary" onClick={salvar007} disabled={salvando007}>
                      {salvando007 ? 'Salvando...' : modoNovo007 ? 'Cadastrar' : 'Salvar alterações'}
                    </button>
                    {msgSalvar007 && (
                      <span style={{
                        fontSize: '0.8rem', fontWeight: 600, padding: '0.35rem 0.8rem', borderRadius: 6,
                        background: msgSalvar007.includes('sucesso') ? '#d1fae5' : '#fee2e2',
                        color: msgSalvar007.includes('sucesso') ? '#065f46' : '#991b1b',
                      }}>
                        {msgSalvar007}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {aba === 'ibs008' && (
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginTop: '1rem' }}>
            <div className="card" style={{ width: 340, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a6ea8' }}>
                  Regras de Emissão
                </h3>
                <button type="button" className="btn btn-primary btn-sm" onClick={novo008}>+ Novo</button>
              </div>
              <div className="field" style={{ marginBottom: '0.75rem' }}>
                <input
                  type="text"
                  placeholder="Código ou descrição..."
                  value={busca008}
                  onChange={e => setBusca008(e.target.value)}
                />
              </div>

              {carregando008 && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Carregando...</p>}
              {!carregando008 && lista008.length === 0 && (
                <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhuma regra encontrada.</p>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
                {lista008.map(item => {
                  const ativo = selecionado008?.ibs08_cod === item.ibs08_cod
                  return (
                    <button
                      key={item.ibs08_cod}
                      onClick={() => selecionar008(item.ibs08_cod)}
                      style={{
                        textAlign: 'left', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', cursor: 'pointer',
                        background: ativo ? '#eff6ff' : '#f8fafc', borderColor: ativo ? '#1a6ea8' : '#e5e7eb',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a6ea8' }}>{item.ibs08_cod}</div>
                      <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: 2 }}>{item.ibs08_des}</div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="card" style={{ flex: 1, minWidth: 0 }}>
              {!selecionado008 && carregandoDetalhe008 && (
                <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Carregando detalhes...</p>
              )}
              {!selecionado008 && !carregandoDetalhe008 && !modoNovo008 && (
                <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                  Selecione uma regra na lista ao lado para ver os detalhes.
                </p>
              )}

              {(selecionado008 || modoNovo008) && (
                <>
                  <h3 style={{
                    fontSize: '1.05rem', fontWeight: 700, color: '#1a6ea8',
                    marginBottom: '1rem', paddingBottom: '0.9rem', borderBottom: '2px solid #eff6ff',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    {modoNovo008 ? 'Nova regra' : selecionado008?.ibs08_cod}
                    {carregandoDetalhe008 && (
                      <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#9ca3af', textTransform: 'none' }}>
                        atualizando...
                      </span>
                    )}
                  </h3>

                  <Secao titulo="Identificação da Regra" cor="#1a6ea8" corFundo="#f7faff">
                    {modoNovo008 ? (
                      <Campo label="Código" value={codigoNovo008} onChange={setCodigoNovo008} width={100} placeholder="0000" />
                    ) : (
                      <Campo label="Código" value={String(selecionado008?.ibs08_cod ?? '')} onChange={() => {}} width={100} />
                    )}
                    <CampoTextarea label="Descrição" value={form008.ibs08_des}
                      onChange={v => setForm008(f => ({ ...f, ibs08_des: v }))} width="100%" linhas={2} />
                    <CampoSel label="CST IBS/CBS" value={form008.ibs08_cst} width={260}
                      onChange={v => setForm008(f => ({ ...f, ibs08_cst: v, ibs08_tri: '' }))}
                      opcoes={[{ value: '', label: 'Selecione...' }, ...listaCst008.map(c => ({ value: c.ibs01_cod, label: `${c.ibs01_cod} — ${c.ibs01_des}` }))]} />
                    <CampoSel label="cClassTrib" value={form008.ibs08_tri} width={260}
                      onChange={v => setForm008(f => ({ ...f, ibs08_tri: v }))}
                      opcoes={[{ value: '', label: 'Selecione...' }, ...listaTri008.filter(t => t.ibs02_cst === form008.ibs08_cst).map(t => ({ value: t.ibs02_cod, label: `${t.ibs02_cod} — ${t.ibs02_des}` }))]} />
                  </Secao>

                  <Secao titulo="Condições de Aplicação" cor="#f07820" corFundo="#fff8f2">
                    <CampoSel label="Documento Fiscal" value={form008.ibs08_dfe} onChange={v => setForm008(f => ({ ...f, ibs08_dfe: v }))} opcoes={OPCOES_DOCUMENTO_FISCAL} width={140} />
                    <CampoSel label="Exige Grupo IBSCBS" value={form008.ibs08_gub} onChange={v => setForm008(f => ({ ...f, ibs08_gub: v }))} opcoes={OPCOES_SIM_NAO} width={160} />
                    <CampoSel label="Compra Governamental" value={form008.ibs08_gov} onChange={v => setForm008(f => ({ ...f, ibs08_gov: v }))} opcoes={OPCOES_SIM_NAO} width={160} />
                    <CampoSel label="Tipo de Operação c/ Ente Governamental" value={form008.ibs08_top} width={320}
                      onChange={v => setForm008(f => ({ ...f, ibs08_top: v }))} opcoes={OPCOES_TIPO_OPERACAO_GOV} />
                    <CampoSel label="Serviço de Transporte" value={form008.ibs08_tra} onChange={v => setForm008(f => ({ ...f, ibs08_tra: v }))} opcoes={OPCOES_SIM_NAO} width={160} />
                    <CampoSel label="Regime Tributário (CRT)" value={form008.ibs08_crt} onChange={v => setForm008(f => ({ ...f, ibs08_crt: v }))} opcoes={OPCOES_REGIME_TRIBUTARIO} width={200} />
                    <CampoSel label="Entidade" value={form008.ibs08_ent} onChange={v => setForm008(f => ({ ...f, ibs08_ent: v }))} opcoes={OPCOES_ENTIDADE} width={200} />
                    <CampoData label="Vigência de" value={form008.ibs08_ivi} onChange={v => setForm008(f => ({ ...f, ibs08_ivi: v }))} width={150} />
                    <CampoData label="Vigência a" value={form008.ibs08_fvi} onChange={v => setForm008(f => ({ ...f, ibs08_fvi: v }))} width={150} />
                  </Secao>

                  <div className="fiscal-tabs">
                    {[
                      { key: 'cbs', label: 'Dados CBS' },
                      { key: 'ibsuf', label: 'IBSUF' },
                      { key: 'ibsmun', label: 'IBSMUN' },
                      { key: 'is', label: 'IS' },
                    ].map(t => (
                      <button key={t.key} type="button"
                        className={`fiscal-tab${subAba008 === t.key ? ' fiscal-tab--ativa' : ''}`}
                        onClick={() => setSubAba008(t.key as typeof subAba008)}>
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {subAba008 === 'cbs' && (
                    <Secao titulo="Dados CBS" cor="#1a6ea8" corFundo="#f7faff">
                      <Campo label="Alíquota Ref. (A)" value={form008.ibs08_cali} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_cali: v }))} />
                      <Campo label="% Red. Base Cálc. (B)" value={form008.ibs08_crbc} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_crbc: v }))} />
                      <Campo label="% Red. Alíquota (C)" value={form008.ibs08_cpra} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_cpra: v }))} />
                      <Campo label="Alíquota Efetiva (A x (1 − C))" value={form008.ibs08_cali && form008.ibs08_crbc
                        ? String(Number(form008.ibs08_cali) * (1 - Number(form008.ibs08_crbc) / 100)) : ''}
                        width={110} onChange={() => {}} />
                      <Campo label="% Diferimento" value={form008.ibs08_cpdi} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_cpdi: v }))} />
                      <Campo label="Cód. Créd. Presumido" value={form008.ibs08_cccp} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_cccp: v }))} />
                      <Campo label="% Créd. Presumido" value={form008.ibs08_cpcp} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_cpcp: v }))} />
                    </Secao>
                  )}

                  {subAba008 === 'ibsuf' && (
                    <Secao titulo="IBSUF" cor="#6b7280" corFundo="#fafafa">
                      <CampoSel label="Estado (UF)" value={form008.ibs08_uest} width={220}
                        onChange={v => setForm008(f => ({ ...f, ibs08_uest: v }))}
                        opcoes={[{ value: '', label: 'Selecione...' }, ...estados.map(e => ({ value: e.estcod, label: `${e.estcod} — ${e.estnom}` }))]} />
                      <Campo label="Alíquota Referência" value={form008.ibs08_uali} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_uali: v }))} />
                      <Campo label="% Red. Base Cálc." value={form008.ibs08_urbc} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_urbc: v }))} />
                      <Campo label="% Red. Alíquota" value={form008.ibs08_upra} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_upra: v }))} />
                      <Campo label="Alíquota Efetiva" value={form008.ibs08_uali && form008.ibs08_urbc
                        ? String(Number(form008.ibs08_uali) * (1 - Number(form008.ibs08_urbc) / 100)) : ''}
                        width={110} onChange={() => {}} />
                      <Campo label="% Diferimento" value={form008.ibs08_updi} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_updi: v }))} />
                      <Campo label="Cód. Créd. Presumido" value={form008.ibs08_uccp} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_uccp: v }))} />
                      <Campo label="% Créd. Presumido" value={form008.ibs08_upcp} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_upcp: v }))} />
                    </Secao>
                  )}

                  {subAba008 === 'ibsmun' && (
                    <Secao titulo="IBSMUN" cor="#6b7280" corFundo="#fafafa">
                      <div style={{ width: 320 }}>
                        <label style={ESTILO_LABEL}>Município</label>
                        <CidadeSelect
                          codValue={form008.ibs08_mmun} desValue={desc008.ibs08_mnom} ibgeValue={form008.ibs08_mibg}
                          filtroUf={form008.ibs08_uest}
                          onChange={(cod, des, ibge) => {
                            setForm008(f => ({ ...f, ibs08_mmun: cod, ibs08_mibg: ibge }))
                            setDesc008(d => ({ ...d, ibs08_mnom: des }))
                          }}
                        />
                      </div>
                      <Campo label="Alíquota Referência" value={form008.ibs08_mali} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_mali: v }))} />
                      <Campo label="% Red. Base Cálc." value={form008.ibs08_mrbc} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_mrbc: v }))} />
                      <Campo label="% Red. Alíquota" value={form008.ibs08_mpra} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_mpra: v }))} />
                      <Campo label="Alíquota Efetiva" value={form008.ibs08_mali && form008.ibs08_mrbc
                        ? String(Number(form008.ibs08_mali) * (1 - Number(form008.ibs08_mrbc) / 100)) : ''}
                        width={110} onChange={() => {}} />
                      <Campo label="% Diferimento" value={form008.ibs08_mpdi} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_mpdi: v }))} />
                      <Campo label="Cód. Créd. Presumido" value={form008.ibs08_mccp} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_mccp: v }))} />
                      <Campo label="% Créd. Presumido" value={form008.ibs08_mpcp} width={110}
                        onChange={v => setForm008(f => ({ ...f, ibs08_mpcp: v }))} />
                    </Secao>
                  )}

                  {subAba008 === 'is' && (
                    <Secao titulo="IS" cor="#6b7280" corFundo="#fafafa">
                      <Campo label="Base de Cálculo do IS" value={form008.ibs08_sbas} width={130}
                        onChange={v => setForm008(f => ({ ...f, ibs08_sbas: v }))} />
                      <Campo label="Alíquota Imp. Seletivo" value={form008.ibs08_sali} width={130}
                        onChange={v => setForm008(f => ({ ...f, ibs08_sali: v }))} />
                    </Secao>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '1rem' }}>
                    <button className="btn btn-primary" onClick={salvar008} disabled={salvando008}>
                      {salvando008 ? 'Salvando...' : modoNovo008 ? 'Cadastrar' : 'Salvar alterações'}
                    </button>
                    {msgSalvar008 && (
                      <span style={{
                        fontSize: '0.8rem', fontWeight: 600, padding: '0.35rem 0.8rem', borderRadius: 6,
                        background: msgSalvar008.includes('sucesso') ? '#d1fae5' : '#fee2e2',
                        color: msgSalvar008.includes('sucesso') ? '#065f46' : '#991b1b',
                      }}>
                        {msgSalvar008}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {aba === 'ibs001' && (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
          <div className="card" style={{ width: 340, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a6ea8' }}>
                Códigos de Situação Tributária
              </h3>
              <button type="button" className="btn btn-primary btn-sm" onClick={novo001}>+ Novo</button>
            </div>
            <div className="field" style={{ marginBottom: '0.75rem' }}>
              <input
                type="text"
                placeholder="Código ou descrição..."
                value={busca001}
                onChange={e => setBusca001(e.target.value)}
              />
            </div>

            {carregando001 && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Carregando...</p>}
            {!carregando001 && lista001.length === 0 && (
              <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhum código encontrado.</p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
              {lista001.map(item => {
                const ativo = selecionado001?.ibs01_cod === item.ibs01_cod
                return (
                  <button
                    key={item.ibs01_cod}
                    onClick={() => selecionar001(item.ibs01_cod)}
                    style={{
                      textAlign: 'left', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', cursor: 'pointer',
                      background: ativo ? '#eff6ff' : '#f8fafc', borderColor: ativo ? '#1a6ea8' : '#e5e7eb',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a6ea8' }}>{item.ibs01_cod}</div>
                    <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: 2 }}>{item.ibs01_des}</div>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="card" style={{ flex: 1, minWidth: 0 }}>
            {!selecionado001 && carregandoDetalhe001 && (
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Carregando detalhes...</p>
            )}
            {!selecionado001 && !carregandoDetalhe001 && (
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                Selecione um código na lista ao lado para ver os detalhes.
              </p>
            )}

            {(selecionado001 || modoNovo001) && (
              <>
                <h3 style={{
                  fontSize: '1.05rem', fontWeight: 700, color: '#1a6ea8',
                  marginBottom: '1rem', paddingBottom: '0.9rem', borderBottom: '2px solid #eff6ff',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  {modoNovo001 ? 'Novo código' : selecionado001?.ibs01_cod}
                  {carregandoDetalhe001 && (
                    <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#9ca3af', textTransform: 'none' }}>
                      atualizando...
                    </span>
                  )}
                </h3>

                <Secao titulo="Identificação" cor="#1a6ea8" corFundo="#f7faff">
                  {modoNovo001 ? (
                    <Campo label="Código" value={codigoNovo001} onChange={setCodigoNovo001} width={90} placeholder="000" />
                  ) : (
                    <Campo label="Código" value={selecionado001?.ibs01_cod ?? ''} onChange={() => {}} width={90} />
                  )}
                  <Campo label="Descrição" value={form001.ibs01_des}
                    onChange={v => setForm001(f => ({ ...f, ibs01_des: v }))} width={420} />
                </Secao>

                <Secao titulo="Indicadores" cor="#f07820" corFundo="#fff8f2" grid>
                  <CampoCheck label="Tributação" checked={form001.ibs01_tri === 'S'} width="100%"
                    onChange={v => setForm001(f => ({ ...f, ibs01_tri: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Monofásica" checked={form001.ibs01_mon === 'S'} width="100%"
                    onChange={v => setForm001(f => ({ ...f, ibs01_mon: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Redução de Alíquotas" checked={form001.ibs01_ral === 'S'} width="100%"
                    onChange={v => setForm001(f => ({ ...f, ibs01_ral: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Diferimento" checked={form001.ibs01_dif === 'S'} width="100%"
                    onChange={v => setForm001(f => ({ ...f, ibs01_dif: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Trans. Crédito" checked={form001.ibs01_tra === 'S'} width="100%"
                    onChange={v => setForm001(f => ({ ...f, ibs01_tra: v ? 'S' : 'N' }))} />
                  <CampoCheck label="CP IBS ZFM" checked={form001.ibs01_zfm === 'S'} width="100%"
                    onChange={v => setForm001(f => ({ ...f, ibs01_zfm: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Ajuste de Competência" checked={form001.ibs01_ajc === 'S'} width="100%"
                    onChange={v => setForm001(f => ({ ...f, ibs01_ajc: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Redutor Base de Cálculo" checked={form001.ibs01_rbc === 'S'} width="100%"
                    onChange={v => setForm001(f => ({ ...f, ibs01_rbc: v ? 'S' : 'N' }))} />
                </Secao>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '1rem' }}>
                  <button className="btn btn-primary" onClick={salvar001} disabled={salvando001}>
                    {salvando001 ? 'Salvando...' : modoNovo001 ? 'Cadastrar' : 'Salvar alterações'}
                  </button>
                  {msgSalvar001 && (
                    <span style={{
                      fontSize: '0.8rem', fontWeight: 600, padding: '0.35rem 0.8rem', borderRadius: 6,
                      background: msgSalvar001.includes('sucesso') ? '#d1fae5' : '#fee2e2',
                      color: msgSalvar001.includes('sucesso') ? '#065f46' : '#991b1b',
                    }}>
                      {msgSalvar001}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {aba === 'ibs002' && (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
          <div className="card" style={{ width: 340, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a6ea8' }}>
                Classificação Tributária
              </h3>
              <button type="button" className="btn btn-primary btn-sm" onClick={novo002}>+ Novo</button>
            </div>
            <div className="field" style={{ marginBottom: '0.75rem' }}>
              <input
                type="text"
                placeholder="Buscar por CST, cClassTrib ou descrição..."
                value={busca002}
                onChange={e => setBusca002(e.target.value)}
              />
            </div>

            {carregando002 && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Carregando...</p>}
            {!carregando002 && lista002.length === 0 && (
              <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhum registro encontrado.</p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
              {lista002.map(item => {
                const ativo = selecionado002?.ibs02_cst === item.ibs02_cst && selecionado002?.ibs02_cod === item.ibs02_cod
                return (
                  <button
                    key={`${item.ibs02_cst}-${item.ibs02_cod}`}
                    onClick={() => selecionar002(item.ibs02_cst, item.ibs02_cod)}
                    style={{
                      textAlign: 'left', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', cursor: 'pointer',
                      background: ativo ? '#eff6ff' : '#f8fafc', borderColor: ativo ? '#1a6ea8' : '#e5e7eb',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a6ea8' }}>
                      {item.ibs02_cst} — {item.ibs02_cod}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: 2 }}>{item.ibs02_des}</div>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="card" style={{ flex: 1, minWidth: 0 }}>
            {!selecionado002 && carregandoDetalhe002 && (
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Carregando detalhes...</p>
            )}
            {!selecionado002 && !carregandoDetalhe002 && !modoNovo002 && (
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                Selecione um registro na lista ao lado para ver os detalhes.
              </p>
            )}

            {(selecionado002 || modoNovo002) && (
              <>
                <h3 style={{
                  fontSize: '1.05rem', fontWeight: 700, color: '#1a6ea8',
                  marginBottom: '1rem', paddingBottom: '0.9rem', borderBottom: '2px solid #eff6ff',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  {modoNovo002 ? 'Novo cClassTrib' : `${selecionado002?.ibs02_cst} — ${selecionado002?.ibs02_cod}`}
                  {carregandoDetalhe002 && (
                    <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#9ca3af', textTransform: 'none' }}>
                      atualizando...
                    </span>
                  )}
                </h3>

                <Secao titulo="Identificação" cor="#1a6ea8" corFundo="#f7faff">
                  {modoNovo002 ? (
                    <>
                      <CampoSel label="CST IBS/CBS" value={cstNovo002} onChange={setCstNovo002}
                        opcoes={[{ value: '', label: 'Selecione...' }, ...opcoesCstIbs001]} width={280} />
                      <Campo label="cClassTrib" value={codNovo002} onChange={setCodNovo002} width={110} placeholder="000000" />
                    </>
                  ) : (
                    <>
                      <Campo label="CST IBS/CBS" value={selecionado002?.ibs02_cst ?? ''} onChange={() => {}} width={90} />
                      <Campo label="cClassTrib" value={selecionado002?.ibs02_cod ?? ''} onChange={() => {}} width={110} />
                    </>
                  )}
                  <Campo label="Descrição Reduzida" value={form002.ibs02_des}
                    onChange={v => setForm002(f => ({ ...f, ibs02_des: v }))} width={340} />
                  <CampoTextarea label="Descrição Completa" value={form002.ibs02_com}
                    onChange={v => setForm002(f => ({ ...f, ibs02_com: v }))} width="100%" linhas={2} />
                  <Campo label="LC 214/25" value={form002.ibs02_lc}
                    onChange={v => setForm002(f => ({ ...f, ibs02_lc: v }))} width={200} />
                </Secao>

                <Secao titulo="Alíquota" cor="#6b7280" corFundo="#fafafa">
                  <CampoSel label="Tipo de Alíquota" value={form002.ibs02_ali} opcoes={OPCOES_TIPO_ALIQUOTA}
                    onChange={v => setForm002(f => ({ ...f, ibs02_ali: v }))} width={180} />
                  <Campo label="% Redução IBS" value={form002.ibs02_rib}
                    onChange={v => setForm002(f => ({ ...f, ibs02_rib: v }))} width={110} />
                  <Campo label="% Redução CBS" value={form002.ibs02_rcb}
                    onChange={v => setForm002(f => ({ ...f, ibs02_rcb: v }))} width={110} />
                </Secao>

                <Secao titulo="Indicadores" cor="#f07820" corFundo="#fff8f2" grid>
                  <CampoCheck label="Base de Cálculo" checked={form002.ibs02_bas === 'S'} width="100%"
                    onChange={v => setForm002(f => ({ ...f, ibs02_bas: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Tributação Regular" checked={form002.ibs02_tre === 'S'} width="100%"
                    onChange={v => setForm002(f => ({ ...f, ibs02_tre: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Crédito Presumido" checked={form002.ibs02_pre === 'S'} width="100%"
                    onChange={v => setForm002(f => ({ ...f, ibs02_pre: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Estorno de Crédito" checked={form002.ibs02_ecr === 'S'} width="100%"
                    onChange={v => setForm002(f => ({ ...f, ibs02_ecr: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Monofásico Padrão" checked={form002.ibs02_mop === '1'} width="100%"
                    onChange={v => setForm002(f => ({ ...f, ibs02_mop: v ? '1' : '0' }))} />
                  <CampoCheck label="Monofásico Retenção" checked={form002.ibs02_mor === '1'} width="100%"
                    onChange={v => setForm002(f => ({ ...f, ibs02_mor: v ? '1' : '0' }))} />
                  <CampoCheck label="Monofásico Ret" checked={form002.ibs02_moe === '1'} width="100%"
                    onChange={v => setForm002(f => ({ ...f, ibs02_moe: v ? '1' : '0' }))} />
                  <CampoCheck label="Monofásico Dif" checked={form002.ibs02_dif === '1'} width="100%"
                    onChange={v => setForm002(f => ({ ...f, ibs02_dif: v ? '1' : '0' }))} />
                </Secao>

                <Secao titulo="Outros" cor="#1a6ea8" corFundo="#f7faff">
                  <CampoSel label="Crédito Para" value={form002.ibs02_crp} opcoes={OPCOES_CREDITO_PARA}
                    onChange={v => setForm002(f => ({ ...f, ibs02_crp: v }))} width={140} />
                  <Campo label="Número Anexo NCM" value={form002.ibs02_anx}
                    onChange={v => setForm002(f => ({ ...f, ibs02_anx: v }))} width={130} />
                </Secao>

                <Secao titulo="Vigência" cor="#6b7280" corFundo="#fafafa">
                  <CampoData label="Início da Vigência" value={form002.ibs02_ivi}
                    onChange={v => setForm002(f => ({ ...f, ibs02_ivi: v }))} width={160} />
                  <CampoData label="Fim da Vigência" value={form002.ibs02_fvi}
                    onChange={v => setForm002(f => ({ ...f, ibs02_fvi: v }))} width={160} />
                  <CampoData label="Data da Atualização" value={form002.ibs02_dat}
                    onChange={v => setForm002(f => ({ ...f, ibs02_dat: v }))} width={180} />
                </Secao>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '1rem' }}>
                  <button className="btn btn-primary" onClick={salvar002} disabled={salvando002}>
                    {salvando002 ? 'Salvando...' : modoNovo002 ? 'Cadastrar' : 'Salvar alterações'}
                  </button>
                  {msgSalvar002 && (
                    <span style={{
                      fontSize: '0.8rem', fontWeight: 600, padding: '0.35rem 0.8rem', borderRadius: 6,
                      background: msgSalvar002.includes('sucesso') ? '#d1fae5' : '#fee2e2',
                      color: msgSalvar002.includes('sucesso') ? '#065f46' : '#991b1b',
                    }}>
                      {msgSalvar002}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {aba === 'ibs004' && (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
          <div className="card" style={{ width: 340, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a6ea8' }}>
                Crédito Presumido
              </h3>
              <button type="button" className="btn btn-primary btn-sm" onClick={novo004}>+ Novo</button>
            </div>
            <div className="field" style={{ marginBottom: '0.75rem' }}>
              <input
                type="text"
                placeholder="Código ou descrição..."
                value={busca004}
                onChange={e => setBusca004(e.target.value)}
              />
            </div>

            {carregando004 && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Carregando...</p>}
            {!carregando004 && lista004.length === 0 && (
              <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhum código encontrado.</p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
              {lista004.map(item => {
                const ativo = selecionado004?.ibs04_cod === item.ibs04_cod
                return (
                  <button
                    key={item.ibs04_cod}
                    onClick={() => selecionar004(item.ibs04_cod)}
                    style={{
                      textAlign: 'left', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', cursor: 'pointer',
                      background: ativo ? '#eff6ff' : '#f8fafc', borderColor: ativo ? '#1a6ea8' : '#e5e7eb',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a6ea8' }}>{item.ibs04_cod}</div>
                    <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: 2 }}>{item.ibs04_des}</div>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="card" style={{ flex: 1, minWidth: 0 }}>
            {!selecionado004 && carregandoDetalhe004 && (
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Carregando detalhes...</p>
            )}
            {!selecionado004 && !carregandoDetalhe004 && !modoNovo004 && (
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                Selecione um código na lista ao lado para ver os detalhes.
              </p>
            )}

            {(selecionado004 || modoNovo004) && (
              <>
                <h3 style={{
                  fontSize: '1.05rem', fontWeight: 700, color: '#1a6ea8',
                  marginBottom: '1rem', paddingBottom: '0.9rem', borderBottom: '2px solid #eff6ff',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  {modoNovo004 ? 'Novo código' : selecionado004?.ibs04_cod}
                  {carregandoDetalhe004 && (
                    <span style={{ fontSize: '0.72rem', fontWeight: 500, color: '#9ca3af', textTransform: 'none' }}>
                      atualizando...
                    </span>
                  )}
                </h3>

                <Secao titulo="Identificação" cor="#1a6ea8" corFundo="#f7faff">
                  {modoNovo004 ? (
                    <Campo label="Código" value={codigoNovo004} onChange={setCodigoNovo004} width={90} placeholder="00" />
                  ) : (
                    <Campo label="Código" value={selecionado004?.ibs04_cod ?? ''} onChange={() => {}} width={90} />
                  )}
                  <CampoTextarea label="Descrição" value={form004.ibs04_des}
                    onChange={v => setForm004(f => ({ ...f, ibs04_des: v }))} width="100%" linhas={2} />
                </Secao>

                <Secao titulo="Indicadores" cor="#f07820" corFundo="#fff8f2" grid>
                  <CampoCheck label="Apropria DFE" checked={form004.ibs04_apd === 'S'} width="100%"
                    onChange={v => setForm004(f => ({ ...f, ibs04_apd: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Apropria Evento" checked={form004.ibs04_ape === 'S'} width="100%"
                    onChange={v => setForm004(f => ({ ...f, ibs04_ape: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Deduz Valor Total" checked={form004.ibs04_dvt === 'S'} width="100%"
                    onChange={v => setForm004(f => ({ ...f, ibs04_dvt: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Aplica IBS" checked={form004.ibs04_ibs === 'S'} width="100%"
                    onChange={v => setForm004(f => ({ ...f, ibs04_ibs: v ? 'S' : 'N' }))} />
                  <CampoCheck label="Aplica CBS" checked={form004.ibs04_cbs === 'S'} width="100%"
                    onChange={v => setForm004(f => ({ ...f, ibs04_cbs: v ? 'S' : 'N' }))} />
                </Secao>

                <Secao titulo="Alíquotas e Referências" cor="#6b7280" corFundo="#fafafa">
                  <CampoSel label="Alíquota CBS" value={form004.ibs04_acb} opcoes={OPCOES_ALIQUOTA_CREDPRES}
                    onChange={v => setForm004(f => ({ ...f, ibs04_acb: v }))} width={360} />
                  <CampoSel label="Alíquota IBS" value={form004.ibs04_aib} opcoes={OPCOES_ALIQUOTA_IBS_CREDPRES}
                    onChange={v => setForm004(f => ({ ...f, ibs04_aib: v }))} width={360} />
                  <CampoSel label="Percentual de CredPresCBS" value={form004.ibs04_pal} opcoes={OPCOES_PALIQ_CREDPRES_CBS}
                    onChange={v => setForm004(f => ({ ...f, ibs04_pal: v }))} width={280} />
                  <CampoSel label="cClass nota referenciada" value={form004.ibs04_cla} opcoes={OPCOES_CCLASS_REFERENCIADA}
                    onChange={v => setForm004(f => ({ ...f, ibs04_cla: v }))} width={280} />
                </Secao>

                <Secao titulo="Vigência" cor="#1a6ea8" corFundo="#f7faff">
                  <CampoData label="Data Início IBS" value={form004.ibs04_ivg}
                    onChange={v => setForm004(f => ({ ...f, ibs04_ivg: v }))} width={160} />
                  <CampoData label="Data Fim IBS" value={form004.ibs04_fvg}
                    onChange={v => setForm004(f => ({ ...f, ibs04_fvg: v }))} width={160} />
                  <CampoData label="Data Início CBS" value={form004.ibs04_ivi}
                    onChange={v => setForm004(f => ({ ...f, ibs04_ivi: v }))} width={160} />
                  <CampoData label="Data Fim CBS" value={form004.ibs04_fvi}
                    onChange={v => setForm004(f => ({ ...f, ibs04_fvi: v }))} width={160} />
                </Secao>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '1rem' }}>
                  <button className="btn btn-primary" onClick={salvar004} disabled={salvando004}>
                    {salvando004 ? 'Salvando...' : modoNovo004 ? 'Cadastrar' : 'Salvar alterações'}
                  </button>
                  {msgSalvar004 && (
                    <span style={{
                      fontSize: '0.8rem', fontWeight: 600, padding: '0.35rem 0.8rem', borderRadius: 6,
                      background: msgSalvar004.includes('sucesso') ? '#d1fae5' : '#fee2e2',
                      color: msgSalvar004.includes('sucesso') ? '#065f46' : '#991b1b',
                    }}>
                      {msgSalvar004}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
